import { FC, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import ReactModal from 'react-modal';
import Decimal from 'decimal.js';
import Button, { ButtonStyle, ButtonType } from '../../../components/Button/Button';
import Content from '../../../components/Content/Content';
import OptionsToggle from '../../../components/OptionsToggle/OptionsToggle';
import SvgIcon from '../../../components/SvgIcon/SvgIcon';
import { MerchantOrderStatus } from '../../../interfaces/MerchantOrder';
import { getOrderData, getPaidAmount } from '../../../redux/OrderSlice/OrderSelectors';
import { fetchOrderStatus } from '../../../redux/OrderSlice/OrderThunks';
import { useAppDispatch } from '../../../redux/Store';
import OrderBody, { OrderCountdown } from './OrderBody/OrderBody';
import FooterActions from '../../../redux/FooterSlice/FooterActions';
import errorIcon from '../../../images/status/failure.svg';
import pendingIcon from '../../../images/status/pending.svg';
import successIcon from '../../../images/status/success.svg';

import styles from './OrderView.module.scss';
import InvoiceModal from './InvoiceModal/InvoiceModal';
import TestIds from '../../../tests/TestIds';
import { formatPrecision, toDecimal } from '../../../helpers/currencyHelper/currencyHelper';
import ProgressBar from '../../../components/ProgressBar/ProgressBar';

const messages = defineMessages({
	metaDescription: {
		id: 'payments.order_meta',
		defaultMessage: 'Order information',
	},
	orderExpired: {
		id: 'payments.order_expired',
		defaultMessage: 'Order has expired',
	},
	expiredNote1: {
		id: 'payments.order_expired_note_1',
		defaultMessage:
			"We didn't detect your payment within the allowed time. Please restart the payment process to complete your order",
	},
	expiredNote2: {
		id: 'payments.order_expired_note_2',
		defaultMessage: "If you've already paid, contact us with your transaction details.",
	},
	orderFailed: {
		id: 'payments.order_failed',
		defaultMessage: 'Order payment has failed',
	},
	orderPending: {
		id: 'payments.order_pending',
		defaultMessage: 'Order payment pending',
	},
	orderPaid: {
		id: 'payments.order_paid',
		defaultMessage: 'Order has been paid',
	},
	paidNote: {
		id: 'payments.order_paid_note',
		defaultMessage:
			'We have received full payment for your order. Processing will commence immediately.',
	},
	backToMerchant: {
		id: 'payments.back_to_merchant',
		defaultMessage: 'Back to merchant',
	},
	forwardInvoice: {
		id: 'payments.forward_invoice',
		defaultMessage: 'Get invoice',
	},
	payWithSc: {
		id: 'payments.pay_with_sc',
		defaultMessage: 'Pay with SpectroCoin',
	},
	amountRemaining: {
		id: 'payments.amount_remaining',
		defaultMessage: 'Amount remaining',
	},
	totalAmount: {
		id: 'payments.total_amount',
		defaultMessage: 'Total payment amount',
	},
	pendingNote: {
		id: 'payments.order_will_be_completed',
		defaultMessage: 'The order will be completed upon full payment',
	},
	paymentDetails: {
		id: 'payments.payment_details',
		defaultMessage: 'Payment details',
	},
	orderStatus: {
		id: 'payments.order_status',
		defaultMessage: 'Order status',
	},
	infoDisclaimer: {
		id: 'payments.info_disclaimer',
		defaultMessage:
			'Payments processed by SpectroCoin Commerce. Be sure you trust the merchant listed, as transactions are final and refunds are handled by the merchant.',
	},
});

enum OrderStatusTab {
	PAYMENT_DETAILS = 'paymentDetails',
	ORDER_STATUS = 'orderStatus',
}

const calculateProgress = (current: string, total: string) => {
	const isDate = (val: string) => val.includes('T') && val.includes(':');
	if (isDate(current) && isDate(total)) {
		const start = new Date(current).getTime();
		const end = new Date(total).getTime();
		const now = Date.now();

		if (Number.isNaN(start) || Number.isNaN(end)) {
			return 0;
		}

		if (end <= start) return 100;
		const progress = ((now - start) / (end - start)) * 100;
		return Math.min(Math.max(Math.floor(progress), 0), 100);
	}

	return 100 - toDecimal(current).div(toDecimal(total)).mul(100).floor().toNumber();
};

const Amount: FC = () => {
	const { formatMessage } = useIntl();
	const { payAmount, payCurrencyCode } = useSelector(getOrderData)!;
	const paidAmount = useSelector(getPaidAmount)!;
	const toPay = Decimal.max(toDecimal(payAmount).sub(paidAmount), 0);
	const progress = useMemo(
		() => calculateProgress(toPay.toString(), payAmount),
		[toPay, payAmount]
	);

	return (
		<div className={styles.amountsBlock}>
			<div className={styles.amountBlock}>
				<label className={styles.amountLabel}>
					{formatMessage(messages.amountRemaining)}
				</label>
				<span className={styles.amount}>
					{`${formatPrecision(toPay.toString(), payCurrencyCode)} ${payCurrencyCode}`}
				</span>
			</div>
			<ProgressBar
				progressPercentage={progress}
				progressClassName={styles.progress}
				reversed
			/>
			<div className={styles.amountBlock}>
				<span className={styles.amount}>{`${formatPrecision(
					payAmount,
					payCurrencyCode
				)} ${payCurrencyCode}`}</span>
				<label className={styles.amountLabel}>{formatMessage(messages.totalAmount)}</label>
			</div>
		</div>
	);
};

const PendingOrderStatusView: FC<{
	link?: string | null;
	message: string | JSX.Element;
	icon: string;
}> = ({ link, message, icon, ...rest }) => {
	const { formatMessage } = useIntl();
	const { created, validUntil, payAmount } = useSelector(getOrderData)!;
	const paidAmount = useSelector(getPaidAmount)!;
	const toPay = Decimal.max(toDecimal(payAmount).sub(paidAmount), 0);
	const [progress, setProgress] = useState(calculateProgress(created, validUntil));
	const [activeTab, setActiveTab] = useState(OrderStatusTab.ORDER_STATUS);

	const handleTabChange = (value: string) => {
		setActiveTab(value as OrderStatusTab);
	};

	const showAmount = toPay.greaterThan(0);

	useEffect(() => {
		const interval = setInterval(
			() => setProgress(calculateProgress(created, validUntil)),
			100
		);
		return () => {
			clearInterval(interval);
		};
	}, [created, validUntil]);

	return (
		<div className={styles.status} {...rest}>
			<OrderCountdown endDate={validUntil} />
			<ProgressBar reversed progressPercentage={progress} />
			<div className={styles.optionsWrapper}>
				<OptionsToggle
					options={[
						{
							label: formatMessage(messages.paymentDetails),
							value: OrderStatusTab.PAYMENT_DETAILS,
						},
						{
							label: formatMessage(messages.orderStatus),
							value: OrderStatusTab.ORDER_STATUS,
						},
					]}
					value={activeTab}
					onChange={handleTabChange}
				/>
			</div>

			{activeTab === OrderStatusTab.PAYMENT_DETAILS && (
				<OrderBody isProgressBarDisabled remainingAmount={toPay.toString()} />
			)}
			{activeTab === OrderStatusTab.ORDER_STATUS && (
				<>
					<SvgIcon className={styles.icon} src={icon} />
					<div className={styles.message}>{message}</div>
					{showAmount && <Amount />}
					{showAmount && (
						<div className={styles.infoDisclaimer}>
							{formatMessage(messages.infoDisclaimer)}
						</div>
					)}
					{link && (
						<Button
							isFullWidth
							data-cy={TestIds.OrderStatusLink}
							className={styles.button}
							type={ButtonType.ANCHOR_LINK}
							link={link}
						>
							<FormattedMessage {...messages.backToMerchant} />
						</Button>
					)}
				</>
			)}
		</div>
	);
};

const OrderStatusView: FC<{
	link?: string | null;
	message: string | JSX.Element;
	icon: string;
	showInvoice?: boolean;
}> = ({ link, message, icon, showInvoice = false, ...rest }) => {
	const [invoiceOpen, setInvoiceOpen] = useState(false);
	const { id } = useSelector(getOrderData)!;

	return (
		<div className={styles.status} {...rest}>
			<SvgIcon className={styles.icon} src={icon} />
			<div className={styles.message}>{message}</div>
			{link && (
				<Button
					isFullWidth
					data-cy={TestIds.OrderStatusLink}
					className={styles.button}
					type={ButtonType.ANCHOR_LINK}
					link={link}
				>
					<FormattedMessage {...messages.backToMerchant} />
				</Button>
			)}
			{false && showInvoice && (
				<>
					<Button
						data-cy={TestIds.OrderStatusGetInvoice}
						className={styles.button}
						type={ButtonType.BUTTON}
						buttonStyle={ButtonStyle.SECONDARY_WITHOUT_BORDER}
						onClick={() => setInvoiceOpen(true)}
					>
						<FormattedMessage {...messages.forwardInvoice} />
					</Button>
					<ReactModal
						className={styles.modal}
						isOpen={invoiceOpen}
						shouldCloseOnOverlayClick
						onRequestClose={() => setInvoiceOpen(false)}
						style={{
							overlay: {
								backgroundColor: '#343E551E',
							},
						}}
					>
						<InvoiceModal onClose={() => setInvoiceOpen(false)} orderId={id} />
					</ReactModal>
				</>
			)}
		</div>
	);
};

const OrderView: FC = () => {
	const { locale, formatMessage } = useIntl();
	const dispatch = useAppDispatch();

	const { id, projectName, payCurrencyCode, status, successURL, failureURL, depositAddress } =
		useSelector(getOrderData)!;

	useEffect(() => {
		const poll =
			[
				MerchantOrderStatus.NEW,
				MerchantOrderStatus.PENDING,
				MerchantOrderStatus.PARTIAL_PAYMENT,
				MerchantOrderStatus.FAILED,
			].includes(status) &&
			(() => {
				dispatch(fetchOrderStatus(id));
				return setInterval(() => dispatch(fetchOrderStatus(id)), 10000);
			})();
		return () => {
			if (poll) clearInterval(poll);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status]);

	useEffect(() => {
		dispatch(FooterActions.setBuyCurrency(payCurrencyCode));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [payCurrencyCode]);

	const content = useMemo(() => {
		if (!depositAddress && status === MerchantOrderStatus.NEW) {
			window.location.assign(`/${locale}/payments/orders/${id}`);
			return null;
		}
		switch (status) {
			case MerchantOrderStatus.NEW:
				return <OrderBody />;
			case MerchantOrderStatus.PAID:
			case MerchantOrderStatus.TEST_PAID:
				return (
					<OrderStatusView
						data-cy={TestIds.OrderStatusPaid}
						icon={successIcon}
						message={
							<div className={styles.statusMessage}>
								<div>{formatMessage(messages.orderPaid)}</div>
								<div className={styles.statusNote}>
									{formatMessage(messages.paidNote)}
								</div>
							</div>
						}
						link={successURL}
						showInvoice
					/>
				);
			case MerchantOrderStatus.PENDING:
			case MerchantOrderStatus.PARTIAL_PAYMENT:
				return (
					<PendingOrderStatusView
						data-cy={TestIds.OrderStatusPending}
						icon={pendingIcon}
						message={
							<div className={styles.statusMessage}>
								<div>{formatMessage(messages.orderPending)}</div>
								<div className={styles.statusNote}>
									{formatMessage(messages.pendingNote)}
								</div>
							</div>
						}
						link={successURL}
					/>
				);
			case MerchantOrderStatus.FAILED:
				return (
					<OrderStatusView
						data-cy={TestIds.OrderStatusFailed}
						icon={errorIcon}
						message={formatMessage(messages.orderFailed)}
						link={failureURL}
					/>
				);
			case MerchantOrderStatus.EXPIRED:
			case MerchantOrderStatus.TEST_EXPIRED:
				return (
					<OrderStatusView
						data-cy={TestIds.OrderStatusExpired}
						icon={errorIcon}
						message={
							<div className={styles.statusMessage}>
								<div>{formatMessage(messages.orderExpired)}</div>
								<div className={styles.statusNote}>
									{formatMessage(messages.expiredNote1)}
								</div>
								<div className={styles.statusNote}>
									{formatMessage(messages.expiredNote2)}
								</div>
							</div>
						}
						link={failureURL}
					/>
				);
			default:
				return null;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formatMessage, status]);

	return (
		<>
			<Helmet
				htmlAttributes={{ lang: locale }}
				title={`SpectroCoin - ${formatMessage(messages.metaDescription)}`}
				meta={[{ name: 'description', content: formatMessage(messages.metaDescription) }]}
			/>
			<Content name={projectName}>
				<div className={styles.container}>{content}</div>
			</Content>
		</>
	);
};

export default OrderView;
